/** 萬事通多對話持久化（MVP：localStorage；形狀預留給之後依 org 同步後端）。 */

export const FALLBACK_KEY = 'velopulse.assistant.msgs';
export const SESSIONS_FALLBACK_KEY = 'velopulse.assistant.sessions';
export const MAX_MSGS = 100;
export const MAX_SESSIONS = 40;
export const DEFAULT_TITLE = '新對話';

export function assistantMsgsKey(orgId, userId) {
  if (orgId && userId) return `${FALLBACK_KEY}.${orgId}.${userId}`;
  return FALLBACK_KEY;
}

export function assistantSessionsKey(orgId, userId) {
  if (orgId && userId) return `${SESSIONS_FALLBACK_KEY}.${orgId}.${userId}`;
  return SESSIONS_FALLBACK_KEY;
}

function nowIso() {
  return new Date().toISOString();
}

function newSessionId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `s_${crypto.randomUUID()}`;
  }
  return `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

export function validConversationId(value) {
  return typeof value === 'string'
    && value.length >= 1
    && value.length <= 80
    && /^[A-Za-z0-9_-]+$/.test(value);
}

export function titleFromMessages(msgs) {
  const first = (msgs || []).find((m) => m.role === 'user' && String(m.text || '').trim());
  const t = String(first?.text || '').replace(/\s+/g, ' ').trim();
  return t ? t.slice(0, 32) : DEFAULT_TITLE;
}

export function makeSession(partial = {}) {
  const createdAt = partial.createdAt || nowIso();
  return {
    id: validConversationId(partial.id) ? partial.id : newSessionId(),
    title: partial.title || DEFAULT_TITLE,
    createdAt,
    updatedAt: partial.updatedAt || createdAt,
    messages: Array.isArray(partial.messages) ? partial.messages.slice(-MAX_MSGS) : [],
  };
}

export function emptyStore() {
  const session = makeSession();
  return { v: 2, currentId: session.id, sessions: [session] };
}

function normalizeStore(parsed) {
  const sessions = (parsed.sessions || []).map((s) => makeSession(s)).filter((s) => s.id);
  if (!sessions.length) return emptyStore();
  const currentId = sessions.some((s) => s.id === parsed.currentId) ? parsed.currentId : sessions[0].id;
  return { v: 2, currentId, sessions: sessions.slice(0, MAX_SESSIONS) };
}

export function loadAssistantMsgs(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.slice(-MAX_MSGS);
  } catch {
    return [];
  }
}

export function saveAssistantMsgs(key, msgs) {
  const capped = Array.isArray(msgs) ? msgs.slice(-MAX_MSGS) : [];
  try {
    if (!capped.length) {
      localStorage.removeItem(key);
      return;
    }
    localStorage.setItem(key, JSON.stringify(capped));
  } catch {
    try {
      localStorage.setItem(key, JSON.stringify(capped.slice(-50)));
    } catch {
      /* quota 仍不足就放棄，不打斷對話 */
    }
  }
}

export function clearAssistantMsgs(key) {
  try { localStorage.removeItem(key); } catch { /* ignore */ }
}

export function loadAssistantStore(sessionsKey, legacyMsgsKey) {
  try {
    const raw = localStorage.getItem(sessionsKey);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && parsed.v === 2 && Array.isArray(parsed.sessions)) {
        return normalizeStore(parsed);
      }
    }
  } catch { /* 落到下方遷移 */ }

  const legacy = loadAssistantMsgs(legacyMsgsKey);
  if (legacy.length) {
    const session = makeSession({
      id: 's-migrated',
      title: titleFromMessages(legacy),
      messages: legacy,
    });
    const store = { v: 2, currentId: session.id, sessions: [session] };
    saveAssistantStore(sessionsKey, store);
    clearAssistantMsgs(legacyMsgsKey);
    return store;
  }
  return emptyStore();
}

export function saveAssistantStore(key, store) {
  const normalized = normalizeStore(store || emptyStore());
  try {
    localStorage.setItem(key, JSON.stringify(normalized));
  } catch {
    try {
      const slim = {
        ...normalized,
        sessions: normalized.sessions.map((s) => ({ ...s, messages: (s.messages || []).slice(-40) })),
      };
      localStorage.setItem(key, JSON.stringify(slim));
    } catch {
      /* quota 仍不足就放棄 */
    }
  }
}

export function currentSession(store) {
  const s = (store?.sessions || []).find((x) => x.id === store.currentId);
  return s || store?.sessions?.[0] || makeSession();
}

export function createSession(store) {
  const cur = currentSession(store);
  if (!(cur.messages || []).length) {
    return { ...store, currentId: cur.id };
  }
  const session = makeSession();
  return {
    v: 2,
    currentId: session.id,
    sessions: [session, ...(store.sessions || [])].slice(0, MAX_SESSIONS),
  };
}

export function switchSession(store, id) {
  if (!(store.sessions || []).some((s) => s.id === id)) return store;
  return { ...store, currentId: id };
}

export function renameSession(store, id, title) {
  const next = String(title || '').replace(/\s+/g, ' ').trim().slice(0, 40) || DEFAULT_TITLE;
  return {
    ...store,
    sessions: (store.sessions || []).map((s) => (
      s.id === id ? { ...s, title: next, updatedAt: nowIso() } : s
    )),
  };
}

export function deleteSession(store, id) {
  const remain = (store.sessions || []).filter((s) => s.id !== id);
  if (!remain.length) return emptyStore();
  const currentId = store.currentId === id ? remain[0].id : store.currentId;
  return { v: 2, currentId, sessions: remain };
}

export function setSessionMessages(store, id, msgs) {
  const capped = Array.isArray(msgs) ? msgs.slice(-MAX_MSGS) : [];
  return {
    ...store,
    sessions: (store.sessions || []).map((s) => {
      if (s.id !== id) return s;
      const title = !s.title || s.title === DEFAULT_TITLE ? titleFromMessages(capped) : s.title;
      return { ...s, messages: capped, title, updatedAt: nowIso() };
    }),
  };
}

/** 從已載入訊息推回 id 序號，避免重新進入頁面後 id 衝突 */
export function maxMsgSeq(msgs) {
  let max = 0;
  for (const m of msgs || []) {
    const n = Number(String(m?.id || '').replace(/^m/i, ''));
    if (Number.isFinite(n) && n > max) max = n;
  }
  return max;
}

/** 換頁後仍把助理回覆寫進 localStorage（不依賴 React 是否還掛著） */
export function appendSessionMessages(sessionsKey, sessionId, extraMsgs) {
  if (!sessionsKey || !sessionId || !Array.isArray(extraMsgs) || !extraMsgs.length) return null;
  const store = loadAssistantStore(sessionsKey);
  const cur = (store.sessions || []).find((s) => s.id === sessionId);
  if (!cur) return null;
  const next = setSessionMessages(store, sessionId, [...(cur.messages || []), ...extraMsgs]);
  saveAssistantStore(sessionsKey, next);
  return next;
}

export function getPendingSendKey(sessionsKey) {
  return `${sessionsKey || SESSIONS_FALLBACK_KEY}.pending`;
}

export function setPendingSend(sessionsKey, payload) {
  try {
    if (!payload) localStorage.removeItem(getPendingSendKey(sessionsKey));
    else localStorage.setItem(getPendingSendKey(sessionsKey), JSON.stringify(payload));
  } catch { /* ignore */ }
}

export function clearPendingSend(sessionsKey, requestToken) {
  try {
    const key = getPendingSendKey(sessionsKey);
    const raw = localStorage.getItem(key);
    if (!raw) return false;
    const current = JSON.parse(raw);
    if (!current || current.requestToken !== requestToken) return false;
    localStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

export function getPendingSend(sessionsKey) {
  try {
    const raw = localStorage.getItem(getPendingSendKey(sessionsKey));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}
